import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usersAPI, projectsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Card, PageHeader, Button, Modal, FormField, Empty, Alert, Loading, StatCard, Badge, Table, PasswordInput } from '../components/ui';
import { Plus, Pencil, Trash2, Search, Building2, MapPin, Receipt, Mail, Phone, User as UserIcon, LayoutGrid, List, MessageSquare, FolderKanban } from 'lucide-react';

const empty = { 
  name: '', email: '', phone: '', title: '', password: '', 
  company_name: '', company_street: '', company_city: '', company_district: '', company_state: '', company_zip: '', 
  invoice_email: '', tax_id: '' 
};

export default function Clients() {
  const { canCreate, canUpdate, canDelete } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState('tiles');

  const [clientProjects, setClientProjects] = useState([]);

  const load = () => { 
    setLoading(true); 
    Promise.all([
      usersAPI.list({ role: 'Client' }),
      projectsAPI.list().catch(() => ({ data: { data: [] } }))
    ]).then(([usersRes, projectsRes]) => {
      setClients((usersRes.data.data || []).filter(u => u.role === 'Client'));
      setClientProjects(projectsRes.data.data || []);
    }).catch(err => {
      console.error(err);
    }).finally(() => setLoading(false)); 
  };
  useEffect(() => { load(); }, []);

  const filtered = search ? clients.filter(c => 
    c.name.toLowerCase().includes(search.toLowerCase()) || 
    (c.email || '').toLowerCase().includes(search.toLowerCase()) || 
    (c.title || '').toLowerCase().includes(search.toLowerCase()) ||
    (c.company_name || '').toLowerCase().includes(search.toLowerCase())
  ) : clients;

  const openCreate = () => { setEditing(null); setForm(empty); setError(''); setModal(true); };
  const openEdit = (c) => { 
    setEditing(c); 
    setForm({ 
      name: c.name, 
      email: c.email||'', 
      phone: c.phone||'', 
      title: c.title||'', 
      password: '',
      company_name: c.company_name||'',
      company_street: c.company_street||'',
      company_city: c.company_city||'',
      company_district: c.company_district||'',
      company_state: c.company_state||'',
      company_zip: c.company_zip||'',
      invoice_email: c.invoice_email||'',
      tax_id: c.tax_id||''
    }); 
    setError(''); setModal(true); 
  };

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      const payload = { ...form, role: 'Client' };
      if (editing) {
        if (!payload.password) delete payload.password;
        await usersAPI.update(editing.user_id, payload);
      } else {
        await usersAPI.create(payload);
      }
      setModal(false); load();
    } catch (err) { setError(err.response?.data?.message || 'Error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this client?')) return;
    await usersAPI.delete(id); load();
  };

  const totalCompanies = new Set(clients.map(c => c.company_name).filter(Boolean)).size;

  return (
    <div className="animate-in" style={{ paddingBottom: 40 }}>
      <PageHeader 
        title="Clients" 
        subtitle="Manage your clients, companies, and billing details" 
        eyebrow="CRM" 
      />

      {/* Stats Section */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
        <StatCard label="Total Clients" value={clients.length} icon={UserIcon} />
        <StatCard label="Companies" value={totalCompanies} icon={Building2} trend="+2 this month" />
        <StatCard label="Billing Profiles" value={clients.filter(c => c.tax_id || c.invoice_email).length} icon={Receipt} />
      </div>

      {/* Toolbar */}
      <Card style={{ marginBottom: 24, padding: '16px 20px', display: 'flex', gap: 16, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 260, maxWidth: 400 }}>
          <Search size={16} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          <input
            placeholder="Search by name, email or company..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ width: '100%', padding: '10px 16px 10px 40px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text)', fontSize: 14 }}
          />
        </div>
        {canCreate && (
          <Button onClick={openCreate} style={{ padding: '10px 20px' }}>
            <Plus size={16} style={{ marginRight: 8 }} /> New Client
          </Button>
        )}
      </Card>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <div style={{ display: 'flex', background: 'var(--bg-elevated)', borderRadius: 'var(--radius-md)', padding: 4, border: '1px solid var(--border)' }}>
          <button 
            type="button" 
            onClick={() => setViewMode('tiles')}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 'var(--radius-sm)', background: viewMode === 'tiles' ? 'var(--bg-hover)' : 'transparent', color: viewMode === 'tiles' ? 'var(--text)' : 'var(--text-muted)', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}
          >
            <LayoutGrid size={16} /> Tiles
          </button>
          <button 
            type="button" 
            onClick={() => setViewMode('list')}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 'var(--radius-sm)', background: viewMode === 'list' ? 'var(--bg-hover)' : 'transparent', color: viewMode === 'list' ? 'var(--text)' : 'var(--text-muted)', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}
          >
            <List size={16} /> List
          </button>
        </div>
      </div>

      {/* Grid / List Content */}
      {loading ? <Loading message="Loading clients..." /> : filtered.length === 0 ? <Empty message="No clients found matching your search." /> : viewMode === 'list' ? (
        <Card>
          <Table 
            columns={[
              { key: 'name', label: 'Client / Contact', render: r => (
                <div 
                  style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}
                  onClick={() => navigate(`/app/clients/${r.user_id}`)}
                >
                  <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 13, flexShrink: 0 }}>{(r.name || r.company_name)?.[0]?.toUpperCase()}</div>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13.5, color: 'var(--accent)' }}>{r.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{r.company_name || (r.title || 'Client')}</div>
                  </div>
                </div>
              )},
              { key: 'contact_info', label: 'Contact Info', render: r => (
                <div>
                  <div style={{ fontSize: 13, color: 'var(--text)' }}>{r.email || '—'}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{r.phone || '—'}</div>
                </div>
              )},
              { key: 'billing_info', label: 'Billing Info', render: r => (
                <div>
                  <div style={{ fontSize: 13, color: 'var(--text)' }}>{r.invoice_email ? `Invoice: ${r.invoice_email}` : '—'}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{r.tax_id ? `Tax ID: ${r.tax_id}` : '—'}</div>
                </div>
              )},
              { key: 'projects', label: 'Projects', render: r => {
                const pList = clientProjects.filter(p => Number(p.client_id) === Number(r.user_id));
                if (pList.length === 0) return <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>—</span>;
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-start' }}>
                    {pList.map(p => (
                      <span 
                        key={p.project_id} 
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/app/projects?project_id=${p.project_id}`);
                        }} 
                        style={{ cursor: 'pointer', background: 'rgba(108,99,255,0.15)', color: 'var(--accent)', border: '1px solid rgba(108,99,255,0.3)', padding: '4px 10px', borderRadius: '6px', fontSize: 12, fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                      >
                        <FolderKanban size={12} />
                        {p.project_name}
                      </span>
                    ))}
                  </div>
                );
              }},
              { key: 'actions', label: '', render: r => (canUpdate || canDelete) ? (
                <div style={{ display: 'flex', gap: 8 }}>
                  {canUpdate && <Button size="sm" variant="ghost" onClick={e => { e.stopPropagation(); openEdit(r); }}><Pencil size={13} /></Button>}
                  {canDelete && <Button size="sm" variant="danger" onClick={e => { e.stopPropagation(); handleDelete(r.user_id); }}><Trash2 size={13} /></Button>}
                </div>
              ) : null}
            ]} 
            data={filtered} 
          />
        </Card>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 20 }}>
          {filtered.map(client => (
            <Card 
              key={client.user_id} 
              className="hover-lift" 
              onClick={() => navigate(`/app/clients/${client.user_id}`)}
              style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', border: '1px solid var(--border)', cursor: 'pointer' }}
            >
              
              {/* Card Header */}
              <div style={{ padding: 20, borderBottom: '1px solid var(--border)', background: 'var(--bg-elevated)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
                  <div style={{ width: 48, height: 48, borderRadius: '12px', background: 'linear-gradient(135deg, var(--accent), #6366f1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 20, fontWeight: 700, boxShadow: '0 4px 12px rgba(99, 102, 241, 0.25)' }}>
                    {(client.name || client.company_name)?.[0]?.toUpperCase()}
                  </div>
                  <div>
                    <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: 'var(--text)' }}>
                      {client.name}
                    </h3>
                    <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>
                      {client.company_name ? client.company_name : 'Individual Client'}
                    </div>
                  </div>
                </div>
                
                {(canUpdate || canDelete) && (
                  <div style={{ display: 'flex', gap: 6 }} onClick={e => e.stopPropagation()}>
                    {canUpdate && <Button size="sm" variant="ghost" onClick={() => openEdit(client)} style={{ padding: 6, color: 'var(--text-muted)' }}><Pencil size={14} /></Button>}
                    {canDelete && <Button size="sm" variant="ghost" onClick={() => handleDelete(client.user_id)} style={{ padding: 6, color: 'var(--danger)' }}><Trash2 size={14} /></Button>}
                  </div>
                )}
              </div>

              {/* Card Body */}
              <div style={{ padding: 20, flex: 1, display: 'flex', flexDirection: 'column', gap: 20 }}>
                
                {/* Contact Section */}
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-muted)', marginBottom: 10 }}>Contact Person</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <UserIcon size={14} color="var(--accent)" />
                    <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--text)' }}>{client.name}</span>
                    {client.title && <Badge variant="neutral" style={{ fontSize: 11 }}>{client.title}</Badge>}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <Mail size={14} color="var(--text-muted)" />
                    <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{client.email || 'No email provided'}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Phone size={14} color="var(--text-muted)" />
                    <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{client.phone || 'No phone provided'}</span>
                  </div>
                </div>

                {/* Address Section */}
                <div style={{ padding: '12px 14px', background: 'var(--bg-elevated)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-light)' }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                    <MapPin size={16} color="var(--accent)" style={{ marginTop: 2, flexShrink: 0 }} />
                    <div style={{ fontSize: 13, color: 'var(--text)', lineHeight: 1.5 }}>
                      {client.company_street ? (
                        <>
                          <div style={{ fontWeight: 500 }}>{client.company_street}</div>
                          <div style={{ color: 'var(--text-muted)' }}>
                            {[client.company_city, client.company_district].filter(Boolean).join(', ')}
                          </div>
                          <div style={{ color: 'var(--text-muted)' }}>
                            {[client.company_state, client.company_zip].filter(Boolean).join(' ')}
                          </div>
                        </>
                      ) : (
                        <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>No address provided</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Billing Section */}
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-muted)', marginBottom: 10 }}>Billing & Tax</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>Tax / VAT ID</div>
                      <div style={{ fontSize: 13, fontWeight: 500, color: client.tax_id ? 'var(--text)' : 'var(--text-muted)' }}>
                        {client.tax_id || '—'}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>Invoice Email</div>
                      <div style={{ fontSize: 13, fontWeight: 500, color: client.invoice_email ? 'var(--text)' : 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {client.invoice_email || '—'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Feedback Section */}
                {(() => {
                  const projectsWithFeedback = clientProjects.filter(p => p.client_id === client.user_id && p.client_feedback);
                  if (projectsWithFeedback.length === 0) return null;
                  return (
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-muted)', marginBottom: 10 }}>Client Feedback</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {projectsWithFeedback.map(p => (
                          <div key={p.project_id} style={{
                            padding: '10px 14px', borderRadius: 'var(--radius-md)',
                            background: 'var(--bg-hover)', border: '1px solid var(--border-light)'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                              <MessageSquare size={13} color="var(--accent)" />
                              <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>{p.project_name}</span>
                            </div>
                            <div style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                              {p.client_feedback}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* Projects Section */}
                <div style={{ marginTop: 'auto', paddingTop: 16, borderTop: '1px solid var(--border-light)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                    <FolderKanban size={14} color="var(--accent)" />
                    <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-muted)' }}>Assigned Projects</span>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {clientProjects.filter(p => Number(p.client_id) === Number(client.user_id)).length === 0 ? (
                      <span style={{ fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic' }}>No projects assigned</span>
                    ) : (
                      clientProjects.filter(p => Number(p.client_id) === Number(client.user_id)).map(p => (
                        <span 
                          key={p.project_id} 
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/app/projects?project_id=${p.project_id}`);
                          }} 
                          style={{
                            cursor: 'pointer',
                            padding: '4px 10px',
                            borderRadius: '6px',
                            fontSize: 12,
                            fontWeight: 500,
                            background: 'rgba(108, 99, 255, 0.15)',
                            color: 'var(--accent)',
                            border: '1px solid rgba(108, 99, 255, 0.3)',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4
                          }}
                        >
                          <FolderKanban size={12} />
                          {p.project_name}
                        </span>
                      ))
                    )}
                  </div>
                </div>

              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Modal */}
      <Modal open={modal} onClose={() => setModal(false)} title={editing ? 'Edit Client Profile' : 'New Client Profile'} width={720}>
        <Alert type="error" message={error} />
        <form onSubmit={handleSave}>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <FormField label="Contact Person (Name)"><input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} required placeholder="Full name" /></FormField>
            <FormField label="Job Title"><input value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))} placeholder="e.g. CEO or Manager" /></FormField>
            
            <FormField label="Contact Email"><input type="email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} placeholder="email@example.com" /></FormField>
            <FormField label="Contact Phone"><input value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))} placeholder="+1 234 567 8900" /></FormField>
            
            <FormField label="Company Name"><input value={form.company_name} onChange={e => setForm(f => ({...f, company_name: e.target.value}))} placeholder="Company Legal Name" /></FormField>
            <FormField label="Tax / VAT ID"><input value={form.tax_id} onChange={e => setForm(f => ({...f, tax_id: e.target.value}))} placeholder="e.g. GB123456789" /></FormField>
          </div>

          <div style={{ marginTop: 24, marginBottom: 12, fontSize: 14, fontWeight: 600, color: 'var(--text)', borderBottom: '1px solid var(--border)', paddingBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
            <MapPin size={16} color="var(--accent)" /> Company Address
          </div>
          
          <FormField label="Street Address">
            <input value={form.company_street} onChange={e => setForm(f => ({...f, company_street: e.target.value}))} placeholder="123 Main St, Suite 400" />
          </FormField>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12 }}>
            <FormField label="City"><input value={form.company_city} onChange={e => setForm(f => ({...f, company_city: e.target.value}))} placeholder="City" /></FormField>
            <FormField label="District"><input value={form.company_district} onChange={e => setForm(f => ({...f, company_district: e.target.value}))} placeholder="District" /></FormField>
            <FormField label="State"><input value={form.company_state} onChange={e => setForm(f => ({...f, company_state: e.target.value}))} placeholder="State" /></FormField>
            <FormField label="ZIP Code"><input value={form.company_zip} onChange={e => setForm(f => ({...f, company_zip: e.target.value}))} placeholder="12345" /></FormField>
          </div>
          
          <div style={{ marginTop: 24, marginBottom: 12, fontSize: 14, fontWeight: 600, color: 'var(--text)', borderBottom: '1px solid var(--border)', paddingBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Receipt size={16} color="var(--accent)" /> Billing Details
          </div>

          <FormField label="Invoice/Billing Email (Optional)">
            <input type="email" value={form.invoice_email} onChange={e => setForm(f => ({...f, invoice_email: e.target.value}))} placeholder="billing@example.com" />
          </FormField>

          {!editing && (
            <FormField label="Portal Password"><PasswordInput value={form.password} onChange={e => setForm(f => ({...f, password: e.target.value}))} placeholder="••••••••" required /></FormField>
          )}

          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 32 }}>
            <Button variant="secondary" onClick={() => setModal(false)} type="button" style={{ padding: '10px 24px' }}>Cancel</Button>
            <Button type="submit" disabled={saving} style={{ padding: '10px 24px' }}>{saving ? 'Saving...' : editing ? 'Update Profile' : 'Create Client'}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
